import React, { Fragment, useEffect, useRef, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { defaultFilters, filterOptions } from './config'
import { getUsersWMS } from './config/api'
import { User } from '../../interfaces/User/UserWMS'
import { ApiResponse } from '../../typings/ApiResponse'
import { useApi } from '../../hooks/useApi'
import { Status } from '../../hooks/useFetchState'
import { usePagination } from '../../hooks/usePagination'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { TableColumn } from '../../components/_EOS/GenericTable/types'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { YesNoBadge } from '../../components/YesNoBadge'
import { UserCreateModal, UserCreateHandle } from './config/modal/UserCreate'
import { UserEditModal, UserEditHandle } from './config/modal/UserEdit'
import EditIcon from '@mui/icons-material/Edit'

const columns: TableColumn<User>[] = [
  { key: 'userAlias', title: 'Nombre' },
  { key: 'userName', title: 'Email' },
  {
    key: 'active',
    title: 'Activo',
    render: (value) => <YesNoBadge value={value as boolean} />
  }
]

interface UserPageProps {}

export const UserPageNew: React.FC<UserPageProps> = () => {
  const [selectedFilters, setSelectedFilters] = React.useState<Filter[]>(defaultFilters)
  const { state, callApi } = useApi<ApiResponse<User>>()
  const pagination = usePagination(10)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const createRef = useRef<UserCreateHandle>(null)
  const editRef = useRef<UserEditHandle>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const editModal = useModal<{ user: User }>('')

  const requestUsers = (filters?: Filter[], page?: number, pageSize?: number) => {
    const emailFilter = filters?.find((f) => f.key === 'email')?.value as string | undefined
    const userIdFilter = filters?.find((f) => f.key === 'name')?.value as string | undefined
    const activeFilter = filters?.find((f) => f.key === 'active')?.value as string | undefined
    callApi(getUsersWMS(page, pageSize, emailFilter, userIdFilter, activeFilter)).catch(console.log)
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestUsers(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleSuccess = (msg: string) => {
    setCreateLoading(false)
    setEditLoading(false)
    openSnackBar(msg, true)
    setCreateOpen(false)
    editModal.closeModal()
    requestUsers(selectedFilters, pagination.page, pagination.pageSize)
  }

  const handleError = (msg: string) => {
    setCreateLoading(false)
    setEditLoading(false)
    openSnackBar(msg, false)
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)
    requestUsers(selectedFilters, 0, pagination.pageSize)
  }, [])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          options={filterOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={requestUsers}
          includeSearchOnClean={true}
          includeAddButton={true}
          onAddButtonClick={() => setCreateOpen(true)}
        />

        <GenericTableV2<User>
          data={state.data?.items || []}
          columns={columns}
          keyField='userId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePaginationChange
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              icon: <EditIcon />,
              onClick: (row) => editModal.openModal('sm', { user: row })
            }
          ]}
        />
      </StandardPage>

      {/* Modal Crear */}
      <Modal
        open={createOpen}
        onClose={() => !createLoading && setCreateOpen(false)}
        maxWidth='sm'
        title='Crear Usuario'
        loading={createLoading}
        successButton={{ action: () => { setCreateLoading(true); createRef.current?.submitForm() }, title: 'Guardar' }}
        cancelButton={{ action: () => setCreateOpen(false), title: 'Cancelar', disabled: createLoading }}
      >
        <UserCreateModal ref={createRef} onSuccess={handleSuccess} onError={handleError} />
      </Modal>

      {/* Modal Editar */}
      <Modal
        open={editModal.isOpen}
        onClose={() => !editLoading && editModal.closeModal()}
        maxWidth='sm'
        title='Editar Usuario'
        loading={editLoading}
        successButton={{ action: () => { setEditLoading(true); editRef.current?.submitForm() }, title: 'Guardar' }}
        cancelButton={{ action: editModal.closeModal, title: 'Cancelar', disabled: editLoading }}
      >
        {editModal.extendedProps?.user && (
          <UserEditModal
            ref={editRef}
            user={editModal.extendedProps.user}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        )}
      </Modal>

      <SnackBar
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
        message={message}
      />
    </Fragment>
  )
}
